// useAddEmployeeFormLogic.ts
import { Employee } from "0.Mine/3.Interfaces/Employee";
import { BusinessService } from "0.Mine/3.Interfaces/Services";
import { SelectChangeEvent } from "@mui/material";
import { useState, useCallback } from "react";

export interface NewEmployee {
  name: string;
  role: string;
  services: string[];
  photo: File;
  email: string;
}

export interface AddEmployeeFormLogicProps {
  onAdd: (employee: Employee) => void;
  defaultSchedule: any;
  employees: Employee[];
  ALL_AVAILABLE_SERVICES: BusinessService[];
}

export interface AddEmployeeFormLogicReturn {
  newEmployee: NewEmployee;
  selectedService: string;
  availableServices: string[];
  handleFieldChange: (field: keyof NewEmployee, value: any) => void;
  handleServiceChange: (index: number, value: string) => void;
  handleServiceSelection: (event: SelectChangeEvent<string>) => void;
  handleAddService: () => void;
  handleRemoveService: (index: number) => void;
  handleAddEmployee: () => void;
}

export const useAddEmployeeFormLogic = ({
  onAdd,
  defaultSchedule,
  employees,
  ALL_AVAILABLE_SERVICES,
}: AddEmployeeFormLogicProps): AddEmployeeFormLogicReturn => {
  const [newEmployee, setNewEmployee] = useState<NewEmployee>({
    name: "",
    role: "",
    services: [],
    photo: new File([""], "default.jpg", { type: "image/jpeg" }),
    email: "",
  });

  const [selectedService, setSelectedService] = useState<string>("");

  // Filter out services that have already been selected
  const availableServices = useCallback(() => {
    return ALL_AVAILABLE_SERVICES.filter(
      (service) => !newEmployee.services.includes(service.name)
    ).map((service) => service.name);
  }, [newEmployee.services, ALL_AVAILABLE_SERVICES]);

  const handleFieldChange = useCallback((field: keyof NewEmployee, value: any) => {
    setNewEmployee((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleServiceChange = useCallback((index: number, value: string) => {
    setNewEmployee((prev) => {
      const updatedServices = [...prev.services];
      updatedServices[index] = value;
      return { ...prev, services: updatedServices };
    });
  }, []);

  // New handler for service dropdown selection
  const handleServiceSelection = useCallback(
    (event: SelectChangeEvent<string>) => {
      const service = event.target.value;
      setSelectedService(service);

      if (service && !newEmployee.services.includes(service)) {
        setNewEmployee((prev) => ({
          ...prev,
          services: [...prev.services, service],
        }));

        // Reset selection after adding
        setTimeout(() => setSelectedService(""), 0);
      }
    },
    [newEmployee.services]
  );

  const handleAddService = useCallback(() => {
    // This is kept for backward compatibility
    // but is no longer needed with the dropdown approach
    setNewEmployee((prev) => ({
      ...prev,
      services: [...prev.services, ""],
    }));
  }, []);

  const handleRemoveService = useCallback((index: number) => {
    setNewEmployee((prev) => {
      const updatedServices = [...prev.services];
      updatedServices.splice(index, 1);
      return { ...prev, services: updatedServices };
    });
  }, []);

  const handleAddEmployee = useCallback(() => {
    // No need to filter empty services since we don't allow them with the dropdown
    // Generate new ID: if employees exist, add 1 to the max id, otherwise use 1
    const newId = employees.length > 0 ? Math.max(...employees.map((emp) => emp.id)) + 1 : 1;

    const servicesId = ALL_AVAILABLE_SERVICES.filter((service) =>
      newEmployee.services.includes(service.name)
    ).map((service) => service._id);
    const employeeToAdd: Employee = {
      id: newId,
      name: newEmployee.name,
      role: newEmployee.role,
      pic: newEmployee.photo,
      serviceNames: newEmployee.services,
      workingHours: { ...defaultSchedule },
      services: servicesId,
      email: newEmployee.email,
    };
    onAdd(employeeToAdd);

    // Reset form state
    setNewEmployee({
      name: "",
      role: "",
      services: [],
      photo: employeeToAdd.pic,
      email: "",
    });
    setSelectedService("");
  }, [newEmployee, employees, defaultSchedule, onAdd]);

  return {
    newEmployee,
    selectedService,
    availableServices: availableServices(),
    handleFieldChange,
    handleServiceChange,
    handleServiceSelection,
    handleAddService,
    handleRemoveService,
    handleAddEmployee,
  };
};
